import React, { memo, useState } from 'react';
import { Dropdown, MenuProps, Typography } from 'antd';
import { formatMoneyExport, toNumber } from '@nxsys/utils/number-helper';
import { showCurrencyByCountry } from '@nxsys/utils';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { AppDispatch, AppState } from '@nxsys/store';
import {
  selectKidValue,
  selectActionVisible,
  selectPreviewFormData,
} from '@nxsys/store/slices/kid/kidSlice';
import { DownOutlined, EyeOutlined, MailOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import { ArrowTightTopIcon } from '@nxsys/components/icons';
import { validateEmail } from '@nxsys/utils/validator';
import { showToast } from '@nxsys/utils/toast';
import { kidAction } from '@nxsys/store/slices/kid/kidSlice';
import PreviewModal from '../../PreviewModal';

type PreviewContentProps = Record<string, never>;
type HeaderActionGroupPropsTypes = {
  handlePreview: (type: 'KID' | 'PAY_ILLUSTRATION') => void;
  handleSentEmail: (type: 'KID' | 'PAY_ILLUSTRATION') => void;
};

const HeaderActionGroup = ({
  handlePreview,
  handleSentEmail,
}: HeaderActionGroupPropsTypes) => {
  const actionVisible = useSelector(selectActionVisible);
  const formData = useSelector(selectPreviewFormData);

  const previewMenuItems: MenuProps['items'] = [
    {
      key: 'preview-kid',
      label: 'KID',
      onClick: () => handlePreview('KID'),
    },
    {
      key: 'preview-pay-illustration',
      label: 'Pay Illustration',
      onClick: () => handlePreview('PAY_ILLUSTRATION'),
    },
  ];

  const sendMenuItems: MenuProps['items'] = [
    {
      key: 'send-kid',
      label: 'KID',
      icon: <MailOutlined />,
      onClick: () => handleSentEmail('KID'),
    },
    {
      key: 'send-pay-illustration',
      label: 'Pay Illustration',
      icon: <MailOutlined />,
      onClick: () => handleSentEmail('PAY_ILLUSTRATION'),
    },
  ];

  return (
    <div className="w-full flex justify-end footer-actions gap-1">
      <Dropdown
        key="preview"
        menu={{ items: previewMenuItems }}
        trigger={['click']}
        disabled={!actionVisible}
      >
        <AppButton
          name="View"
          variant="outlined"
          icon={<EyeOutlined />}
          rightIcon={<DownOutlined />}
          onClick={() => {}}
          disabled={!actionVisible}
        />
      </Dropdown>
      <Dropdown
        key="send"
        menu={{ items: sendMenuItems }}
        trigger={['click']}
        disabled={!actionVisible || !formData?.emailId}
      >
        <AppButton
          name="Send"
          type="primary"
          icon={<ArrowTightTopIcon color="#00795B" />}
          rightIcon={<DownOutlined />}
          iconPosition="start"
          onClick={() => {}}
          disabled={!actionVisible || !formData?.emailId}
        />
      </Dropdown>
    </div>
  );
};

const PreviewContent: React.FC<PreviewContentProps> = memo(() => {
  const data = useSelector(selectKidValue);
  const dispatch = useDispatch<AppDispatch>();
  const formData = useSelector(selectPreviewFormData);
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const currencySymbol = showCurrencyByCountry(payrollCountry);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);

  const handlePreview = async (type: 'KID' | 'PAY_ILLUSTRATION') => {
    if (!!data?.errors?.length) {
      showToast({ type: 'error', message: data?.errors?.join?.('\n') });
      return;
    }
    const requestParams = {
      ...formData,
      emailId: undefined,
      type: type,
    };
    const messageType = type === 'KID' ? 'KID' : 'Pay Illustration';
    try {
      const response = await dispatch(
        kidAction.previewForUmbrella(requestParams),
      ).unwrap();
      setPreviewHtml(response?.data || '');
    } catch (error: any) {
      console.log('error', error);
      const errorMessage = error?.message || `Failed to preview ${messageType}`;
      showToast({ type: 'error', message: errorMessage });
    }
  };

  const handleSentEmail = async (type: 'KID' | 'PAY_ILLUSTRATION') => {
    if (!!data?.errors?.length) {
      showToast({ type: 'error', message: data?.errors?.join?.('\n') });
      return;
    }

    if (!formData?.emailId) {
      showToast({ type: 'error', message: 'Email is required!' });
      return;
    }

    if (!validateEmail(formData?.emailId || '')) {
      showToast({ type: 'error', message: 'Invalid email!' });
      return;
    }
    const requestParams = {
      ...formData,
      type: type,
    };
    const messageType = type === 'KID' ? 'KID' : 'Pay Illustration';
    try {
      await dispatch(kidAction.sendEmailForUmbrella(requestParams)).unwrap();
      showToast({
        type: 'success',
        message: `${messageType} sent successfully!`,
      });
    } catch (error: any) {
      console.log('error', error);
      const errorMessage = error.message || `Failed to send email ${messageType}`;
      showToast({ type: 'error', message: errorMessage });
    }
  };

  // Always show content, even if data is empty
  const isAdvance = data?.holidayPayMethod === 'ROLLED_UP';
  // Advanced holiday pay is already included in taxable pay, not a deduction.
  const rowLetter = (retainedIndex: number) =>
    String.fromCharCode(65 + retainedIndex - (isAdvance ? 1 : 0));

  const totalIncome =
    data?.payRatePerHour && data?.noOfHours
      ? toNumber(data?.payRatePerHour) * toNumber(data?.noOfHours)
      : 0;

  const margin = data?.margin !== undefined ? toNumber(data?.margin) : 0;

  const expenses = data?.expenses !== undefined ? toNumber(data?.expenses) : 0;

  const holidayPayRetained =
    toNumber(data?.retainedHolidayPay || 0) +
    toNumber(data?.retainedHolidayPayErCosts || 0);

  const employersNI =
    data?.employersNI !== undefined ? toNumber(data?.employersNI) : 0;

  const employersPension =
    data?.employersPension !== undefined ? toNumber(data?.employersPension) : 0;

  const appLevy = data?.appLevy !== undefined ? toNumber(data?.appLevy) : 0;

  const taxablePay = data?.taxablePay;

  const tax = data?.tax !== undefined ? toNumber(data?.tax) : 0;

  const employeeNI =
    data?.employeeNI !== undefined ? toNumber(data?.employeeNI) : 0;

  const employeePension =
    data?.employeePension !== undefined ? toNumber(data?.employeePension) : 0;

  const netPay = data?.netPay !== undefined ? toNumber(data?.netPay) : 0;

  return (
    <>
      <div className="flex justify-between items-center px-5 py-3 border-b border-neutral-200">
        <Typography.Title
          level={4}
          className="flex-shrink-0"
          style={{ margin: 0 }}
        >
          Preview
        </Typography.Title>
        <HeaderActionGroup
          handlePreview={handlePreview}
          handleSentEmail={handleSentEmail}
        />
      </div>
      <div className="p-5">
        <div className="border rounded-[8px] mt-[18px]">
          <ul className="table-example-pay">
            <li className="flex py-[16px] border-b">
              <span className="flex-1 pl-[24px]">
                <span className="block font-[500] text-sm">
                  A) Total Income
                </span>
              </span>
              <span className="w-[150px] pl-[24px] font-[500] text-sm">
                {formatMoneyExport(totalIncome, currencySymbol)}
              </span>
              <span className="w-[150px] pl-[12px] font-[400] text-xs text-gray-500">
                -
              </span>
            </li>
            <li className="flex py-[16px] border-b">
              <span className="flex-1 pl-[24px]">
                <span className="block font-[500] text-sm">B) Margin</span>
              </span>
              <span className="w-[150px] pl-[24px] font-[500] text-sm">
                {formatMoneyExport(margin, currencySymbol)}
              </span>
              <span className="w-[150px] pl-[12px] font-[400] text-xs text-gray-500">
                -
              </span>
            </li>
            <li className="flex py-[16px] border-b">
              <span className="flex-1 pl-[24px]">
                <span className="block font-[500] text-sm">C) Expenses</span>
              </span>
              <span className="w-[150px] pl-[24px] font-[500] text-sm">
                {formatMoneyExport(expenses, currencySymbol)}
              </span>
              <span className="w-[150px] pl-[12px] font-[400] text-xs text-gray-500">
                -
              </span>
            </li>
            {!isAdvance && (
              <li className="flex py-[16px] border-b">
                <span className="flex-1 pl-[24px]">
                  <span className="block font-[500] text-sm">
                    D) Holiday Pay
                  </span>
                </span>
                <span className="w-[150px] pl-[24px] font-[500] text-sm">
                  {formatMoneyExport(
                    holidayPayRetained,
                    currencySymbol,

            )}
              </span>
              <span className="w-[150px] pl-[12px] font-[400] text-xs text-gray-500">
                -
              </span>
            </li>
            )}
            <li className="flex py-[16px] border-b">
              <span className="flex-1 pl-[24px]">
                <span className="block font-[500] text-sm">
                  {rowLetter(4)}) Employer National Insurance
                </span>
              </span>
              <span className="w-[150px] pl-[24px] font-[500] text-sm">
                {formatMoneyExport(employersNI, currencySymbol)}
              </span>
              <span className="w-[150px] pl-[12px] font-[400] text-xs text-gray-500">
                -
              </span>
            </li>
            <li className="flex py-[16px] border-b">
              <span className="flex-1 pl-[24px]">
                <span className="block font-[500] text-sm">
                  {rowLetter(5)}) Employer Pension
                </span>
              </span>
              <span className="w-[150px] pl-[24px] font-[500] text-sm">
                {formatMoneyExport(employersPension, currencySymbol)}
              </span>
              <span className="w-[150px] pl-[12px] font-[400] text-xs text-gray-500">
                -
              </span>
            </li>
            <li className="flex py-[16px] border-b">
              <span className="flex-1 pl-[24px]">
                <span className="block font-[500] text-sm">
                  {rowLetter(6)}) Apprenticeship Levy
                </span>
              </span>
              <span className="w-[150px] pl-[24px] font-[500] text-sm">
                {formatMoneyExport(appLevy, currencySymbol)}
              </span>
              <span className="w-[150px] pl-[12px] font-[400] text-xs text-gray-500">
                -
              </span>
            </li>
            <li className="flex py-[16px] border-b">
              <span className="flex-1 pl-[24px]">
                <span className="block font-[500] text-sm">
                  {rowLetter(7)}) Taxable Pay {isAdvance ? '(+ Holiday Pay Advanced)' : ''}
                </span>
              </span>
              <span className="w-[150px] pl-[24px] font-[500] text-sm">
                {formatMoneyExport(taxablePay, currencySymbol)}
              </span>
              <span className="w-[150px] pl-[12px] font-[400] text-sm font-[500]">
                ({isAdvance ? 'A-B-C-D-E-F' : 'A-B-C-D-E-F-G'})
              </span>
            </li>
            <li className="flex py-[16px] border-b">
              <span className="flex-1 pl-[24px]">
                <span className="block font-[500] text-sm">{rowLetter(8)}) Tax</span>
              </span>
              <span className="w-[150px] pl-[24px] font-[500] text-sm">
                {formatMoneyExport(tax, currencySymbol)}
              </span>
              <span className="w-[150px] pl-[12px] font-[400] text-xs text-gray-500">
                -
              </span>
            </li>
            <li className="flex py-[16px] border-b">
              <span className="flex-1 pl-[24px]">
                <span className="block font-[500] text-sm">
                  {rowLetter(9)}) Employee National Insurance
                </span>
              </span>
              <span className="w-[150px] pl-[24px] font-[500] text-sm">
                {formatMoneyExport(employeeNI, currencySymbol)}
              </span>
              <span className="w-[150px] pl-[12px] font-[400] text-xs text-gray-500">
                -
              </span>
            </li>
            <li className="flex py-[16px] border-b">
              <span className="flex-1 pl-[24px]">
                <span className="block font-[500] text-sm">
                  {rowLetter(10)}) Employee Pension
                </span>
              </span>
              <span className="w-[150px] pl-[24px] font-[500] text-sm">
                {formatMoneyExport(employeePension, currencySymbol)}
              </span>
              <span className="w-[150px] pl-[12px] font-[400] text-xs text-gray-500">
                -
              </span>
            </li>
            <li className="flex py-[16px]">
              <span className="flex-1 pl-[24px]">
                <span className="block font-[500] text-sm">
                  {rowLetter(11)}) Net Pay{' '}
                  {expenses
                    ? `(Incl ${formatMoneyExport(expenses, currencySymbol)} Expenses)`
                    : ''}
                </span>
              </span>
              <span className="w-[150px] pl-[24px] font-[500] text-sm">
                {formatMoneyExport(netPay, currencySymbol)}
              </span>
              <span className="w-[150px] pl-[12px] font-[500] text-sm">
                ({`${isAdvance ? 'G-H-I-J' : 'H-I-J-K'}${expenses ? '+ C' : ''}`})
              </span>
            </li>
          </ul>
        </div>
      </div>
      <PreviewModal html={previewHtml} onClose={() => setPreviewHtml(null)} />
    </>
  );
});

export default PreviewContent;
